import { useState, useEffect } from 'react';
import { Popover, Stack, Group, Text, Checkbox, Select, ActionIcon, Loader, Badge, Divider } from '@mantine/core';
import { IconCheck, IconX } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { groupsApi } from '../../api/client';
import { permissionsApi, CRUDPermission } from '../../api/permissionsApi';
import type { Group as GroupType } from '../../types/group';

interface PermissionPopoverProps {
  path: string;
  targetGroupId: string | null;
  opened: boolean;
  onClose: () => void;
  onSave: (groupId: string, permissions: CRUDPermission[]) => Promise<void>;
  children: React.ReactNode;
}

const OPS: { value: CRUDPermission; label: string; letter: string; color: string }[] = [
  { value: 'read', label: 'Просмотр', letter: 'R', color: 'blue' },
  { value: 'create', label: 'Создание', letter: 'C', color: 'green' },
  { value: 'update', label: 'Редактирование', letter: 'U', color: 'orange' },
  { value: 'delete', label: 'Удаление', letter: 'D', color: 'red' },
];

export function PermissionPopover({ path, targetGroupId, opened, onClose, onSave, children }: PermissionPopoverProps) {
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(targetGroupId);
  const [permissions, setPermissions] = useState<CRUDPermission[]>([]);
  const [saving, setSaving] = useState(false);
  const [allGroupsPerms, setAllGroupsPerms] = useState<Record<string, CRUDPermission[]>>({});
  const queryClient = useQueryClient();

  const { data: groups = [] } = useQuery<GroupType[]>({
    queryKey: ['groups'],
    queryFn: async () => {
      const res = await groupsApi.list();
      return res.data?.data || [];
    },
  });

  // Load permissions for ALL groups when popover opens
  useEffect(() => {
    if (opened && groups.length > 0) {
      const loadAllPerms = async () => {
        const permsMap: Record<string, CRUDPermission[]> = {};
        for (const g of groups) {
          if (g.is_system && g.name.toLowerCase() !== 'superadmins') continue;
          try {
            const perms = await permissionsApi.getGroupPermissions(g.id);
            if (perms[path] && perms[path].length > 0) {
              permsMap[g.id] = perms[path];
            }
          } catch {}
        }
        setAllGroupsPerms(permsMap);
      };
      loadAllPerms();
    }
  }, [opened, groups, path]);

  // Load current permissions when group changes
  useEffect(() => {
    if (selectedGroupId && opened) {
      permissionsApi.getGroupPermissions(selectedGroupId).then(perms => {
        setPermissions(perms[path] || []);
      });
    } else if (!selectedGroupId) {
      setPermissions([]);
    }
  }, [selectedGroupId, path, opened]);

  useEffect(() => {
    if (targetGroupId) {
      setSelectedGroupId(targetGroupId);
    }
  }, [targetGroupId]);

  const handleToggle = (op: CRUDPermission) => {
    setPermissions(prev => 
      prev.includes(op) ? prev.filter(p => p !== op) : [...prev, op]
    );
  };

  const handleSave = async () => {
    if (!selectedGroupId) return;
    setSaving(true);
    try {
      await onSave(selectedGroupId, permissions);
      // Update local cache
      setAllGroupsPerms(prev => ({
        ...prev,
        [selectedGroupId]: permissions.length > 0 ? permissions : [],
      }));
      if (permissions.length === 0) {
        setAllGroupsPerms(prev => {
          const updated = { ...prev };
          delete updated[selectedGroupId];
          return updated;
        });
      }
      queryClient.invalidateQueries({ queryKey: ['ui-permissions'] });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const groupOptions = groups
    .filter(g => !g.is_system || g.name.toLowerCase() === 'superadmins')
    .map(g => ({ value: g.id, label: g.name }));

  // Groups that have permissions on this path
  const groupsWithPerms = Object.entries(allGroupsPerms)
    .filter(([, perms]) => perms.length > 0)
    .map(([gId, perms]) => ({
      group: groups.find(g => g.id === gId),
      perms,
    }))
    .filter(x => x.group);

  return (
    <Popover opened={opened} onClose={onClose} position="bottom" withArrow shadow="md">
      <Popover.Target>
        {children}
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="sm" style={{ minWidth: 280 }}>
          <Text c="dimmed" style={{ wordBreak: 'break-all', fontSize: 'var(--font-size-xs)' }}>{path}</Text>
          
          {/* Show groups that already have permissions */}
          {groupsWithPerms.length > 0 && (
            <>
              <Text fw={500} style={{ fontSize: 'var(--font-size-xs)' }}>Группы с правами:</Text>
              <Stack gap={4}>
                {groupsWithPerms.map(({ group, perms }) => (
                  <Group key={group!.id} gap="xs" justify="space-between">
                    <Text 
                      size="xs" 
                      style={{ cursor: 'pointer' }}
                      c={selectedGroupId === group!.id ? 'blue' : undefined}
                      fw={selectedGroupId === group!.id ? 600 : undefined}
                      onClick={() => setSelectedGroupId(group!.id)}
                    >
                      {group!.name}
                    </Text>
                    <Group gap={2}>
                      {OPS.map(op => (
                        <Badge 
                          key={op.value} 
                          size="xs" 
                          variant={perms.includes(op.value) ? 'filled' : 'light'}
                          color={perms.includes(op.value) ? op.color : 'gray'}
                        >
                          {op.letter}
                        </Badge>
                      ))}
                    </Group>
                  </Group>
                ))}
              </Stack>
              <Divider />
            </>
          )}
          
          <Select
            label="Редактировать группу"
            placeholder="Выберите группу"
            data={groupOptions}
            value={selectedGroupId}
            onChange={setSelectedGroupId}
            size="xs"
            searchable
          />

          {selectedGroupId && (
            <Stack gap="xs">
              {OPS.map(op => (
                <Checkbox
                  key={op.value}
                  label={`${op.letter} - ${op.label}`}
                  checked={permissions.includes(op.value)}
                  onChange={() => handleToggle(op.value)}
                  size="xs"
                  color={op.color}
                />
              ))}
            </Stack>
          )}

          <Group justify="flex-end" gap="xs">
            <ActionIcon variant="light" color="gray" onClick={onClose} size="sm">
              <IconX size={14} />
            </ActionIcon>
            <ActionIcon 
              variant="filled" 
              color="green" 
              onClick={handleSave} 
              size="sm"
              disabled={!selectedGroupId || saving}
            >
              {saving ? <Loader size={14} /> : <IconCheck size={14} />}
            </ActionIcon>
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}

export default PermissionPopover;
